import { useEffect, useState } from 'react';

// ============================================================
// 內容載入（2026-09-04 安全重構）
// 舊：import.meta.glob eager——全部內容焗入 public JS bundle（裸奔）
// 新：Cloudflare Access 登入後 fetch /api/knowhow/all——
//     knowhow-vault Worker 驗 Cf-Access-Jwt-Assertion 先吐內容
// 對外介面（useMarkdownContent / getMarkdownFiles / helpers）簽名不變，
// 各 TAB 代碼零改動；未載入好時返回 null / {}（原有空態處理）
// ============================================================

let STORE: Record<string, string> | null = null;
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();

export function ensureKnowhowContent(): Promise<void> {
  if (STORE) return Promise.resolve();
  if (!loading) {
    loading = fetch('/api/knowhow/all')
      .then((r) => {
        if (!r.ok) throw new Error(`內容服務 HTTP ${r.status}`);
        return r.json() as Promise<{ success: boolean; content: Record<string, string> }>;
      })
      .then((d) => {
        if (!d.success || !d.content) throw new Error('內容服務回應異常');
        STORE = d.content;
        listeners.forEach((fn) => fn());
      })
      .catch((e) => {
        loading = null;
        throw e;
      });
  }
  return loading;
}

function useStoreReady(): boolean {
  const [ready, setReady] = useState<boolean>(STORE !== null);
  useEffect(() => {
    if (STORE) {
      setReady(true);
      return;
    }
    const fn = () => setReady(true);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  return ready;
}

export function useMarkdownContent(path: string): string | null {
  const ready = useStoreReady();
  if (!ready || !STORE) return null;
  if (STORE[path]) return STORE[path];
  const fullPath = path.startsWith('/src/content') ? path : `/src/content${path}`;
  return STORE[fullPath] ?? null;
}

// Get all markdown files in a directory
export function getMarkdownFiles(directory: string): Record<string, string> {
  if (!STORE) return {};
  const prefix = directory.startsWith('/src/content') ? directory : `/src/content${directory}`;
  const result: Record<string, string> = {};
  for (const [path, content] of Object.entries(STORE)) {
    if (path.startsWith(prefix + '/')) {
      const relativePath = path.slice(prefix.length + 1);
      result[relativePath] = content;
    }
  }
  return result;
}

// Extract title from markdown content (first # heading)
export function extractTitle(content: string): string {
  const match = content.match(/^#\s+(.+)$/m);
  return match ? match[1].trim() : 'Untitled';
}

// Extract description from markdown content (first paragraph after title)
export function extractDescription(content: string): string {
  const lines = content.split('\n');
  let inFrontmatter = false;
  let titleFound = false;

  for (const line of lines) {
    const trimmed = line.trim();

    // Skip frontmatter
    if (trimmed === '---') {
      inFrontmatter = !inFrontmatter;
      continue;
    }
    if (inFrontmatter) continue;

    // Skip title
    if (!titleFound && trimmed.startsWith('# ')) {
      titleFound = true;
      continue;
    }

    // Skip empty lines
    if (!trimmed) continue;

    // Skip other headings
    if (trimmed.startsWith('#')) continue;

    // Skip blockquotes
    if (trimmed.startsWith('>')) continue;

    // Return first content paragraph
    return trimmed.slice(0, 200) + (trimmed.length > 200 ? '...' : '');
  }

  return '';
}

// Parse rule filename to extract number and name
export function parseRuleFilename(filename: string): { number: string; name: string; file: string } {
  const match = filename.match(/^(\d+)_(.+)\.md$/);
  if (match) {
    return {
      number: match[1],
      name: match[2].replace(/_/g, ' '),
      file: filename,
    };
  }
  return {
    number: '00',
    name: filename.replace('.md', ''),
    file: filename,
  };
}
